import { createAsyncThunk } from '@reduxjs/toolkit';
import { format, isBefore, parse } from 'date-fns';
import { NavigateFunction } from 'react-router-dom';

import { BUSINESS_DETAILS_ROUTE, QUOTE_ROUTE } from '../../constants/routes';
import { quotesApi } from '../../services/quotes';
import { refDataApi } from '../../services/refData';
import { updateRetrieveQuoteId } from '../../slices/analytics/trackAnalyticsSlice';
import {
  updateIndustryOnly,
  updateOccupation,
  updateOccupationCategoryOnly,
  updatePerils,
} from '../../slices/businessDetails';
import { updateDynamicCoverQuestions } from '../../slices/coverDetails';
import {
  updateAccessToken,
  updateQuoteDateUpdated,
  updateQuoteRetrievalError,
  updateRetrieveQuoteFlow,
} from '../../slices/quote';
import { addressApiSlice, AppDispatch, RootState } from '../../store';
import { Perils, RiskRatingResponse } from '../../types/address';
import { RetrieveQuoteFlow } from '../../types/enums';
import { PolicyResponse, RiskLocation } from '../../types/policy';
import { RiskClassCode } from '../../types/risk';
import { DateFormat, transformRiskRatingResponse } from '../../utils';
import { navigateAction } from '../navigateAction';
import { quotePremiumsAction } from '../quotePremiumsAction';
import { getPastEffectiveDatePayload } from './getPastEffectiveDatePayload';
import { getRiskByClassCode } from './getRiskByClassCode';
import { getDateFromString, populateQuote } from './populateQuote';

class QuoteRetrievalError extends Error {
  constructor(message: string = 'Quote retrieval failed') {
    super(message);
  }
}

class OccupationAnzsicNotFoundError extends Error {
  constructor() {
    super('Occupation code not found');
  }
}

class OccupationNotFoundError extends Error {
  constructor(anzsic: string) {
    super(`Occupation code not found for anzsic ${anzsic}`);
  }
}

class EntityNotFoundError extends Error {
  constructor(
    public type: string,
    public code: string,
    message = `${type}:${code} not found`
  ) {
    super(message);
  }
}

const updateIndustryAction = async ({
  industryCode,
  effectiveDate,
  dispatch,
}: {
  industryCode: string;
  effectiveDate: string;
  dispatch: AppDispatch;
}) => {
  const { industries } = await dispatch(
    refDataApi.endpoints?.getIndustries.initiate({ effectiveDate })
  ).unwrap();
  const entity = industries.find((industry) => industry.industryCode === industryCode);
  if (!entity) throw new EntityNotFoundError('Industry', industryCode);
  dispatch(updateIndustryOnly(entity));
  return entity;
};

const updateOccupationCategoriesAction = async ({
  industryCode,
  categoryCode,
  effectiveDate,
  dispatch,
}: {
  industryCode: string;
  categoryCode: string;
  effectiveDate: string;
  dispatch: AppDispatch;
}) => {
  const { occupationCategories } = await dispatch(
    refDataApi.endpoints?.getOccupationCategories.initiate({
      effectiveDate,
      industryCode,
    })
  ).unwrap();
  const entity = occupationCategories.find((e) => e.categoryCode === categoryCode);
  if (!entity) throw new EntityNotFoundError('OccupationCategory', categoryCode);
  dispatch(updateOccupationCategoryOnly(entity));
  return entity;
};

const updatePerilsDataAction = async ({
  location,
  effectiveDate,
  dispatch,
}: {
  location: RiskLocation;
  effectiveDate: string;
  dispatch: AppDispatch;
}) => {
  const result = await dispatch(
    addressApiSlice.endpoints.riskRatingLookup.initiate({
      requestPayload: {
        streetLine1: location.addressLine1,
        city: location.city,
        stateCode: location.stateCode,
        postalCode: location.postalCode,
        effectiveDate,
        meta: {
          channel: 'QBE Standard',
          product: 'bpk',
          transaction: 'submission',
        },
      },
    })
  ).unwrap();
  const perils = transformRiskRatingResponse(result as unknown as RiskRatingResponse) as Perils;
  dispatch(updatePerils(perils));
  return perils;
};

const updateDynamicCoverQuestionsAction = async ({
  anzsicCode,
  effectiveDate,
  dispatch,
}: {
  anzsicCode: string;
  effectiveDate: string;
  dispatch: AppDispatch;
}) => {
  try {
    const { covers } = await dispatch(
      refDataApi.endpoints?.getDyanmicCoverQuestions.initiate({
        effectiveDate,
        anzsicCode,
      })
    ).unwrap();
    dispatch(updateDynamicCoverQuestions(covers));
    return covers;
  } catch {
    dispatch(updateDynamicCoverQuestions([]));
    return [];
  }
};

const getOccupationCode = (policyData: PolicyResponse) => {
  const liabilityRisk = getRiskByClassCode(policyData.policy.risks, RiskClassCode.Liability);

  if (!liabilityRisk) {
    return undefined;
  }

  const liabilityDetails = liabilityRisk.riskDetail.riskBPKLiability;

  return liabilityDetails.occupationCode;
};

export const retrieveQuoteAction = createAsyncThunk<
  void,
  {
    quoteNumber: string;
    email: string;
    accessToken: string;
    navigate?: NavigateFunction;
    qasMigrationFlag?: boolean;
  },
  {
    dispatch: AppDispatch;
    state: RootState;
  }
>('retrieveQuote', async ({ quoteNumber, email, accessToken, navigate }, { dispatch }) => {
  try {
    dispatch(updateAccessToken(accessToken));
    const policyResponse = await dispatch(
      quotesApi.endpoints?.getQuote.initiate({
        quoteNumber,
        email,
        accessToken,
      })
    ).unwrap();
    const { riskEffectiveDate: inceptionDate } = policyResponse.policy.risks[0];
    if (!inceptionDate) {
      throw new QuoteRetrievalError('inception date not found in quote');
    }
    const formattedInceptionDate = getDateFromString(inceptionDate);
    if (!formattedInceptionDate) {
      throw new QuoteRetrievalError('inception date not valid');
    }

    let bootstrapPayload = policyResponse;
    let isPastEffectiveDate = false;
    let effectiveDate: string;

    const today = new Date().setHours(0, 0, 0);
    const todayFormatted = format(today, DateFormat.yyyyMMdd);
    const parsedDate = parse(formattedInceptionDate, DateFormat.yyyyMMdd, today).setHours(
      23,
      59,
      59
    );

    if (isBefore(parsedDate, today)) {
      if (quoteNumber) dispatch(updateRetrieveQuoteId({ retrieveQuoteId: quoteNumber }));
      dispatch(updateRetrieveQuoteFlow(RetrieveQuoteFlow.PastEffectiveDate));
      const pastEffectiveDatePayload = getPastEffectiveDatePayload(policyResponse, todayFormatted);

      effectiveDate = getDateFromString(todayFormatted)!;

      isPastEffectiveDate = true;
      bootstrapPayload = pastEffectiveDatePayload;
    } else {
      if (quoteNumber) dispatch(updateRetrieveQuoteId({ retrieveQuoteId: quoteNumber }));
      dispatch(updateRetrieveQuoteFlow(RetrieveQuoteFlow.Success));
      effectiveDate = getDateFromString(formattedInceptionDate)!;
    }

    const occupationCode = getOccupationCode(bootstrapPayload);
    if (!occupationCode) throw new OccupationAnzsicNotFoundError();

    const occupationListItems = await dispatch(
      refDataApi.endpoints?.getOccupationsList.initiate({
        effectiveDate,
        anzsics: [occupationCode],
      })
    ).unwrap();
    const occupationItem = occupationListItems.occupations.find(
      (occ) => occ.code === occupationCode
    );
    if (!occupationItem) throw new OccupationNotFoundError(occupationCode);

    const occupationItems = await dispatch(
      refDataApi.endpoints?.getOccupations.initiate({
        effectiveDate,
        industryCode: occupationItem.industryCode,
        occupationCategory: occupationItem.categoryCode,
      })
    ).unwrap();

    const occupation = occupationItems.occupations.find((o) => o.anzsicCode === occupationCode);
    if (!occupation) throw new OccupationNotFoundError(occupationCode);

    dispatch(updateOccupation(occupation));

    await Promise.all([
      updateIndustryAction({
        industryCode: occupationItem.industryCode,
        effectiveDate,
        dispatch,
      }),
      updateOccupationCategoriesAction({
        industryCode: occupationItem.industryCode,
        categoryCode: occupationItem.categoryCode,
        effectiveDate,
        dispatch,
      }),
      updatePerilsDataAction({
        location: getRiskByClassCode(bootstrapPayload.policy.risks, RiskClassCode.Liability)
          .location,
        effectiveDate,
        dispatch,
      }),
      updateDynamicCoverQuestionsAction({
        anzsicCode: occupationCode,
        effectiveDate,
        dispatch,
      }),
    ]);

    populateQuote({
      data: bootstrapPayload,
      occupation,
      dispatch,
    });

    if (isPastEffectiveDate) {
      await dispatch(quotePremiumsAction()).unwrap();
      dispatch(updateQuoteDateUpdated(true));
    }
    if (navigate) {
      dispatch(
        navigateAction({
          to: QUOTE_ROUTE,
          navigate,
        })
      );
    }
  } catch (e) {
    console.log(e);
    dispatch(updateRetrieveQuoteId({ retrieveQuoteId: quoteNumber }));
    dispatch(updateQuoteRetrievalError(true));
    dispatch(updateRetrieveQuoteFlow(RetrieveQuoteFlow.Failed));
    if (navigate) {
      dispatch(
        navigateAction({
          to: BUSINESS_DETAILS_ROUTE,
          navigate,
        })
      );
    }
  }
});
